const number = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const format = (value) => {
  const parsed = number(value);
  return parsed === null ? '—' : parsed.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const unique = (values) => [...new Set(values.filter(Boolean))];

const decisionLabels = {
  watch_buy: 'BUY RULES MATCHED',
  watch_sell: 'SELL RULES MATCHED',
  watch_short: 'SHORT SETUP FOUND',
  watch_cover: 'COVER RULES MATCHED',
  no_action: 'NO SIGNAL',
  unavailable: 'DATA UNAVAILABLE',
};

export const strategyDecisionLabel = (decision) => decisionLabels[decision] || decision;

const entryFailureReasons = (family, indicators, evidence = {}) => {
  const value = (key) => number(indicators?.[key]);
  const close = value('close');
  const reasons = [];

  if (family === 'trend-pullback') {
    if (close !== null && value('sma_100') !== null && close <= value('sma_100')) reasons.push('The price is not above its longer-term trend average.');
    if (close !== null && value('sma_20') !== null && close <= value('sma_20')) reasons.push('The price has not recovered above its shorter-term trend average.');
    if (value('macd_12_26') !== null && value('macd_12_26') <= 0) reasons.push('The strategy’s momentum confirmation is not positive.');
    if (value('rsi_14') !== null && (value('rsi_14') < 40 || value('rsi_14') > 70)) reasons.push('Momentum is outside this strategy’s permitted recovery range.');
  }

  if (family === 'momentum-breakout') {
    if (close !== null && value('prior_high_50') !== null && close <= value('prior_high_50')) reasons.push('The stock did not close above its previous 50-session high.');
    if (value('sma_20') !== null && value('sma_50') !== null && value('sma_20') <= value('sma_50')) reasons.push('The shorter trend is not above the longer trend.');
    if (value('rsi_14') !== null && value('rsi_14') > 85) reasons.push('Momentum is currently too extended for this strategy.');
    const currentVolume = number(evidence.current_volume);
    const priorVolume = value('prior_volume_mean_50');
    if (currentVolume !== null && priorVolume !== null && currentVolume < 1.2 * priorVolume) reasons.push('Trading volume was not strong enough for the breakout rule.');
  }

  if (family === 'controlled-mean-reversion') {
    if (close !== null && value('sma_100') !== null && close <= value('sma_100')) reasons.push('The stock is not currently above its longer-term trend average.');
    if (close !== null && value('sma_20') !== null && close > 0.97 * value('sma_20')) reasons.push('The pullback is not deep enough for this strategy.');
    if (value('rsi_14') !== null && value('rsi_14') > 35) reasons.push('Momentum is not weak enough for this pullback rule.');
    if (close !== null && close > 0 && value('atr_14') !== null && value('atr_14') / close > 0.06) reasons.push('Recent price movement is too volatile for this controlled entry.');
  }

  if (family === 'moving-average-crossover') {
    if (close !== null && value('sma_50') !== null && close <= value('sma_50')) reasons.push('The price is not above the longer-term average.');
    if (close !== null && value('sma_50') !== null && close > value('sma_50')) reasons.push('No fresh upward 20/50 average crossover was recorded.');
  }

  return unique(reasons).slice(0, 3);
};

const entrySummary = {
  'trend-pullback': 'The recovery-in-an-uptrend entry rules matched on this completed daily bar.',
  'momentum-breakout': 'The new-high breakout, trend, volume, and momentum safeguards matched.',
  'controlled-mean-reversion': 'The unusually deep pullback and recovery conditions matched.',
  'moving-average-crossover': 'A fresh upward 20/50 trend crossover and price confirmation matched.',
};

const exitSummary = (audit) => {
  const rules = audit?.triggered_rules || [];
  const shortClose = audit?.exit_signal === 'exit_short';
  if (rules.includes('exit.stop_loss')) return 'The daily-close stop-loss rule requested an exit for the next eligible market open.';
  if (rules.includes('exit.take_profit')) return 'The daily-close take-profit rule requested an exit for the next eligible market open.';
  if (rules.includes('exit.maximum_holding_period')) return shortClose
    ? 'This virtual short reached the strategy’s maximum holding period. Review the proposal to cover it at the next eligible market open.'
    : 'This position reached the strategy’s maximum holding period. Review the proposal to close it at the next eligible market open.';
  if (rules.some((rule) => String(rule).startsWith('exit.'))) return 'The strategy’s technical exit rules requested an exit for the next eligible market open.';
  return audit?.reason || 'The strategy requested an exit for the next eligible market open.';
};

export const explainStrategyCheck = ({ item, audit, family }) => {
  if (!audit) return 'The immutable Strategy Check evidence is unavailable.';
  if (item.pending_action === 'enter_long') return 'An entry is already scheduled for the next eligible market open, so no duplicate entry was created.';
  if (['exit_long', 'exit_short'].includes(item.pending_action)) return 'A position close is already scheduled for the next eligible market open, so no duplicate close was created.';
  if (item.decision === 'unavailable') return audit.reason || 'Required completed market data was unavailable, so the bot took no action.';
  if (item.decision === 'watch_buy') return entrySummary[family] || audit.reason;
  if (['watch_sell', 'watch_cover'].includes(item.decision)) return exitSummary(audit);
  if (audit?.input_context?.position) return audit.reason || 'The position remains open because no configured exit rule matched.';
  const reasons = entryFailureReasons(family, audit.indicators, audit.report_evidence);
  if (reasons.length) return reasons.join(' ');
  const fallback = {
    'trend-pullback': 'The full pullback-and-recovery transition was not present on this completed daily bar.',
    'momentum-breakout': 'The complete new-high, trend, volume, and momentum combination was not present.',
    'controlled-mean-reversion': 'The complete deep-pullback-and-recovery combination was not present. Very selective strategies may wait a long time.',
    'moving-average-crossover': 'A valid fresh upward 20/50 crossover entry transition was not present.',
  };
  return fallback[family] || audit.reason || 'The strategy conditions were not met in this Market Check.';
};

const indicatorLabels = {
  'trend-pullback': [['close', 'Close'], ['sma_20', 'SMA20'], ['sma_100', 'SMA100'], ['macd_12_26', 'MACD12/26'], ['rsi_14', 'RSI14']],
  'momentum-breakout': [['close', 'Close'], ['prior_high_50', 'Previous 50-session high'], ['sma_20', 'SMA20'], ['sma_50', 'SMA50'], ['rsi_14', 'RSI14'], ['prior_volume_mean_50', 'Previous 50-session average volume'], ['prior_low_20', 'Previous 20-session low']],
  'controlled-mean-reversion': [['close', 'Close'], ['sma_20', 'SMA20'], ['sma_100', 'SMA100'], ['rsi_14', 'RSI14'], ['atr_14', 'ATR14']],
  'moving-average-crossover': [['close', 'Close'], ['sma_20', 'SMA20'], ['sma_50', 'SMA50']],
};

export const technicalIndicatorRows = ({ item, audit, family, evaluatorVersion }) => {
  if (evaluatorVersion === 'observer-v1') {
    return [['RSI', item.rsi], ['MACD', item.macd], ['SMA', item.sma], ['ATR', item.atr]];
  }
  const indicators = audit?.indicators || {};
  const rows = (indicatorLabels[family] || Object.keys(indicators).sort().map((key) => [key, key]))
    .map(([key, label]) => [label, indicators[key]]);
  if (family === 'momentum-breakout') {
    const currentVolume = number(audit?.report_evidence?.current_volume);
    const averageVolume = number(indicators.prior_volume_mean_50);
    rows.push(['Current volume', currentVolume]);
    rows.push(['Current / average volume', currentVolume !== null && averageVolume ? currentVolume / averageVolume : null]);
  }
  return rows.map(([label, value]) => [label, format(value)]);
};

export const validateReportIdentity = ({
  bot,
  latestObservation,
  audits,
  strategyIdentity,
  evaluationReferences = [],
  reportVerificationStatus = 'verified',
  preEvaluationFailure = null,
}) => {
  const required = ['strategy_definition_id', 'trading_strategy_name', 'strategy_hash', 'evaluator_version', 'strategy_schema_version'];
  const invalid = { ok: false, auditsBySymbol: new Map(), verificationBySymbol: new Map() };
  if (!bot || required.some((field) => !bot[field])) return invalid;
  if (!latestObservation) return { ok: true, auditsBySymbol: new Map(), verificationBySymbol: new Map(), reportVerificationStatus: 'verified' };
  if (!strategyIdentity || required.some((field) => !strategyIdentity[field])) return invalid;
  if (required.some((field) => String(strategyIdentity[field]) !== String(bot[field]))) return invalid;
  const lineage = latestObservation.details?.strategy_evaluation;
  if (!lineage || lineage.strategy_hash !== bot.strategy_hash || lineage.evaluator_version !== bot.evaluator_version || lineage.strategy_schema_version !== bot.strategy_schema_version) return invalid;
  if (reportVerificationStatus === 'pre_evaluation_failure') {
    const symbols = latestObservation.details?.symbols;
    const affected = preEvaluationFailure?.affected_symbols;
    if (!Array.isArray(lineage.evaluations) || lineage.evaluations.length
      || !Array.isArray(symbols) || !symbols.length || !Array.isArray(affected)
      || symbols.length !== affected.length || audits.length || evaluationReferences.length
      || latestObservation.details?.evaluation_audit != null
      || !['completed_daily_price_unavailable', 'position_split_history_unavailable', 'market_data_unavailable'].includes(preEvaluationFailure.category)
      || symbols.some((item, index) => !item || typeof item !== 'object'
        || item.decision !== 'unavailable' || typeof item.reason !== 'string'
        || !item.reason.trim() || String(item.symbol).toUpperCase() !== affected[index])
      || new Set(affected).size !== affected.length) return invalid;
    return { ok: true, auditsBySymbol: new Map(), verificationBySymbol: new Map(), reportVerificationStatus, preEvaluationFailure };
  }
  if (preEvaluationFailure) return invalid;
  if (!['verified', 'partially_verified'].includes(reportVerificationStatus)) return invalid;
  const lineageReferences = lineage.evaluations || [];
  if (evaluationReferences.length !== lineageReferences.length) return invalid;
  const referenceStatusByKey = new Map((evaluationReferences || []).map((reference) => [
    `${String(reference.symbol || '').toUpperCase()}|${String(reference.fingerprint || '').toLowerCase()}`,
    reference,
  ]));
  const auditByFingerprint = new Map((audits || []).map((audit) => [audit.evaluation_fingerprint, audit]));
  const auditsBySymbol = new Map();
  const verificationBySymbol = new Map();
  let missingCount = 0;
  for (const reference of lineageReferences) {
    const status = referenceStatusByKey.get(`${String(reference.symbol || '').toUpperCase()}|${String(reference.fingerprint || '').toLowerCase()}`);
    if (!status || !['verified', 'missing_historical_evidence'].includes(status.verification_status)) return invalid;
    verificationBySymbol.set(reference.symbol, status);
    if (status.verification_status === 'missing_historical_evidence') {
      missingCount += 1;
      if (bot.evaluator_version !== 'observer-v1' || reportVerificationStatus !== 'partially_verified') return invalid;
      if (status.evaluation_id) return invalid;
      if (auditByFingerprint.has(reference.fingerprint)) return invalid;
      continue;
    }
    const audit = auditByFingerprint.get(reference.fingerprint);
    if (!audit
      || String(audit.id) !== String(status.evaluation_id)
      || String(audit.symbol).toUpperCase() !== String(reference.symbol).toUpperCase()
      || String(audit.strategy_definition_id) !== String(bot.strategy_definition_id)
      || audit.strategy_hash !== bot.strategy_hash
      || audit.evaluator_version !== bot.evaluator_version
      || audit.strategy_schema_version !== bot.strategy_schema_version) return invalid;
    auditsBySymbol.set(reference.symbol, audit);
  }
  if ((reportVerificationStatus === 'partially_verified') !== (missingCount > 0)) return invalid;
  if ((audits || []).length !== lineageReferences.length - missingCount) return invalid;
  if (missingCount > 0 && auditsBySymbol.size === 0) return invalid;
  const symbols = latestObservation.details?.symbols || [];
  if (symbols.some((item) => !verificationBySymbol.has(item.symbol))) return invalid;
  return { ok: true, auditsBySymbol, verificationBySymbol, reportVerificationStatus };
};
